"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { deleteWithAuth, getWithAuth } from "@/lib/api-helpers";
import { openCenteredPopup } from "@/lib/connect-popup";
import { useAuth } from "@/hooks/use-auth";
import { ConnectionCard, ConnectionCardGrid } from "@/components/tailwind/ui/ConnectionCard";
import { AVAILABLE_CONNECTIONS, Connection } from "./connectionTypes";
import ConnectionDetailModal from "./ConnectionDetailModal";
import { BrandIcon } from "@/components/tailwind/ui/BrandIcon";
import DeleteConfirmationModal from "@/components/tailwind/ui/deleteConfirmationModal";
import { SelfAnchoredPopover } from "@/components/tailwind/ui/anchored-popover";
import {
  useCalendarStatus,
  useGithubStatus,
  useSlackStatus,
  useGmailStatus,
  useGoogleDriveStatus,
  useJiraStatus,
  useLinearStatus,
  useIntercomStatus,
  useFigmaStatus,
  useDropboxStatus,
  useZoomStatus,
  useAsanaStatus,
  useTrelloStatus,
  useBoxStatus,
  useClickUpStatus,
  usePagerDutyStatus,
  useNotionStatus,
  useGitLabStatus,
  useConfluenceStatus,
  useOneDriveStatus,
  useSentryStatus,
  useSalesforceStatus,
  useTeamsStatus,
  useOutlookStatus,
  useConnectionsCatalog,
  integrationKeys,
} from "@/hooks/integrations";

type InstallationSummary = {
  id: number;
  accountId: number;
  accountLogin: string;
  repositorySelection: "all" | "selected";
  targetType?: string;
  suspendedAt?: string;
};

type GitHubConnectionStatus = {
  connected: boolean;
  githubLogin?: string;
  githubAvatarUrl?: string;
  installations?: InstallationSummary[];
  updatedAt?: string;
  createdAt?: string;
};


/**
 * Resolve the active workspaceId from the same storage shape the connect
 * handlers use. Returns null when nothing is cached — the calendar / gmail
 * status hooks then skip their fetches rather than firing with an empty
 * workspaceId (which the backend would reject with a 400).
 */
function readWorkspaceIdFromStorage(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const cached = window.localStorage.getItem("cached_workspace");
    if (cached) {
      const ws = JSON.parse(cached);
      const id = ws?._id || ws?.id;
      if (id) return String(id);
    }
  } catch {
    /* ignore */
  }
  return (
    localStorage.getItem("workspaceId") ||
    sessionStorage.getItem("currentWorkspaceId") ||
    ""
  );
}

export default function IntegrationsContent() {
  const { user } = useAuth();
  
  // Resolve workspaceId once per render
  const queryClient = useQueryClient();
  const [workspaceId, setWorkspaceId] = useState<string>("");
  // Which connected integration's "Manage" menu is open (by connection id), or null.
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  // Which integration is mid-delete (by connection id), or null — drives the per-card "Deleting…" label.
  const [disconnectingId, setDisconnectingId] = useState<string | null>(null);

  // Connect popup tracking. The poll is a fallback to refresh status if the
  // user closes the popup without finishing.
  const connectPopupRef = useRef<Window | null>(null);
  const popupPollRef = useRef<number | null>(null);

  const stopPopupPoll = () => {
    if (popupPollRef.current !== null) {
      window.clearInterval(popupPollRef.current);
      popupPollRef.current = null;
    }
  };

  /** Track an open popup: remember it, poll for it closing. */
  const trackPopup = (popup: Window) => {
    connectPopupRef.current = popup;
    stopPopupPoll();
    popupPollRef.current = window.setInterval(() => {
      if (connectPopupRef.current?.closed ?? true) {
        stopPopupPoll();
        connectPopupRef.current = null;
        // Popup closed — re-check statuses so a successful connect shows.
        void queryClient.invalidateQueries({ queryKey: integrationKeys.all });
      }
    }, 600);
  };

  /** Open a connect URL in a centered popup; fall back to a full-page redirect if blocked. */
  const launchConnectPopup = (url: string, name: string) => {
    const popup = openCenteredPopup(url, name);
    if (!popup) {
      window.location.href = url;
      return;
    }
    trackPopup(popup);
  };

  useEffect(() => {
    setWorkspaceId(readWorkspaceIdFromStorage() ?? "");
  }, []);

  // Clean up the popup-close poll if this tab unmounts mid-flow.
  useEffect(() => stopPopupPoll, []);

  // Listen for the connect popup's postMessage. The backend callback now
  // serves a self-closing HTML page that posts {type:'closot-connect', ...}
  // to the opener — that gives us an instant signal to refresh statuses,
  // instead of waiting for the 600ms popup-closed poll.
  useEffect(() => {
    const apiOrigin = (() => {
      const raw = process.env.NEXT_PUBLIC_API_URL;
      if (!raw) return null;
      try {
        return new URL(raw).origin;
      } catch {
        return null;
      }
    })();
    if (!apiOrigin) {
      // Fail closed — never bind the listener when the trust anchor is missing
      // or malformed. Surface to the console so a misconfigured env doesn't
      // silently disable post-install card refresh.
      console.warn(
        "[connect] NEXT_PUBLIC_API_URL is unset or malformed; postMessage trust check disabled. Falling back to 600ms popup-closed poll.",
      );
      return;
    }
    // Positive log so the bound trust anchor is visible at a glance — useful
    // when checking that prod's NEXT_PUBLIC_API_URL made it into the bundle.
    console.log("[connect] api origin bound for postMessage trust:", apiOrigin);

    const onMessage = (event: MessageEvent) => {
      // Only trust messages from our own API origin.
      if (event.origin !== apiOrigin) return;
      const data = event.data;
      if (!data || typeof data !== "object" || (data as { type?: unknown }).type !== "closot-connect") return;

      const status = (data as { status?: unknown }).status;
      const integration = (data as { integration?: unknown }).integration;

      // Refresh every integration's status — generic so this scales as more
      // integrations adopt the same self-closing callback pattern.
      void queryClient.invalidateQueries({ queryKey: integrationKeys.all });

      // Pre-empt the popup-closed poll: stop tracking and close the popup
      // ourselves if it didn't manage to close (some browsers block close()
      // for windows whose opener was nulled by COOP).
      stopPopupPoll();
      if (connectPopupRef.current) {
        try { connectPopupRef.current.close(); } catch { /* ignore */ }
        connectPopupRef.current = null;
      }

      // Surface failures: the popup itself shows a brief "Connection failed"
      // card, but if the user wasn't looking at it (or browser closed it
      // before they could read), they'd otherwise see the card just snap
      // back to "Connect" with no explanation.
      if (status === "error") {
        const label =
          integration === "github" ? "GitHub" :
          integration === "slack" ? "Slack" :
          integration === "gmail" ? "Gmail" :
          integration === "google-calendar" ? "Google Calendar" :
          integration === "google-drive" ? "Google Drive" :
          integration === "jira" ? "Jira" :
          integration === "linear" ? "Linear" :
          integration === "figma" ? "Figma" :
          integration === "dropbox" ? "Dropbox" :
          integration === "zoom" ? "Zoom" :
          integration === "asana" ? "Asana" :
          integration === "trello" ? "Trello" :
          integration === "box" ? "Box" :
          integration === "clickup" ? "ClickUp" :
          integration === "pagerduty" ? "PagerDuty" :
          integration === "notion" ? "Notion" :
          integration === "gitlab" ? "GitLab" :
          integration === "confluence" ? "Confluence" :
          integration === "onedrive" ? "OneDrive" :
          integration === "sentry" ? "Sentry" :
          integration === "salesforce" ? "Salesforce" :
          integration === "teams" ? "Microsoft Teams" :
          integration === "outlook" ? "Outlook" :
          integration === "intercom" ? "Intercom" :
          "Integration";
        alert(`Couldn't connect ${label}. Please try again.`);
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [queryClient]);

  // The connect popup redirects straight to Salesforce, so the org type has
  // to be chosen before it opens.
  const [salesforceEnvOpen, setSalesforceEnvOpen] = useState(false);
  const [salesforceEnv, setSalesforceEnv] = useState<"production" | "sandbox">("production");

  const launchSalesforceConnect = (environment: "production" | "sandbox") => {
    setSalesforceEnvOpen(false);
    const apiUrl = process.env.NEXT_PUBLIC_API_URL || "http://localhost:3002";
    const wsId = readWorkspaceIdFromStorage() || "";
    if (!wsId) {
      alert("Could not connect Salesforce: no active workspace. Try refreshing the page.");
      return;
    }
    launchConnectPopup(
      `${apiUrl}/api/salesforce/connect?workspaceId=${encodeURIComponent(wsId)}&environment=${environment}`,
      "salesforce-connect",
    );
  };

  // Intercom's authorize page is per hosting region, and Google sign-in on
  // the wrong regional host fails outright — so the region has to be chosen
  // before the popup opens, like the Salesforce org type.
  const [intercomRegionOpen, setIntercomRegionOpen] = useState(false);
  const [intercomRegion, setIntercomRegion] = useState<"us" | "eu" | "au">("us");

  const launchIntercomConnect = (region: "us" | "eu" | "au") => {
    setIntercomRegionOpen(false);
    const apiUrl = process.env.NEXT_PUBLIC_API_URL || "http://localhost:3002";
    const wsId = readWorkspaceIdFromStorage() || "";
    if (!wsId) {
      alert("Could not connect Intercom: no active workspace. Try refreshing the page.");
      return;
    }
    launchConnectPopup(
      `${apiUrl}/api/intercom/connect?workspaceId=${encodeURIComponent(wsId)}&region=${region}`,
      "intercom-connect",
    );
  };

  /**
   * Begin (or restart) the OAuth connect flow for an integration. Shared by
   * the "Connect" button on unconnected cards and the "Reconnect" menu item
   * on connected ones — reconnecting just re-runs OAuth, which the backend
   * upserts over the existing record. Uses the same centered-popup flow as
   * the initial connect so it isn't blocked by the browser.
   */
  const startConnect = async (connection: Connection) => {
    const redirectParam = encodeURIComponent("/settings?tab=integrations");
    const apiUrl = process.env.NEXT_PUBLIC_API_URL || "http://localhost:3002";

    if (connection.id === "github") {
      launchConnectPopup(`${apiUrl}/api/github/connect?redirect=${redirectParam}`, "github-connect");
    } else if (connection.id === "slack") {
      // Open the popup synchronously (within the click) so it isn't blocked,
      // then point it at Slack's URL once fetched.
      const popup = openCenteredPopup("about:blank", "slack-connect");
      if (popup) trackPopup(popup);
      const data = await getWithAuth<{ url: string }>("/api/slack/connect");
      if (data && "url" in data) {
        if (popup && !popup.closed) {
          popup.location.href = data.url;
        } else {
          window.location.href = data.url; // popup blocked/closed — fall back
        }
      } else {
        popup?.close();
      }
    } else if (connection.id === "google-calendar") {
      // Like Gmail below, /api/calendar/connect is auth-protected and derives
      // the user's email from the session cookie carried by the popup — we
      // only supply workspaceId.
      const wsId = workspaceId || readWorkspaceIdFromStorage();
      if (!wsId) {
        alert("Could not connect: no active workspace. Try refreshing the page.");
        return;
      }
      launchConnectPopup(
        `${apiUrl}/api/calendar/connect?workspaceId=${encodeURIComponent(wsId)}`,
        "calendar-connect",
      );
    } else if (connection.id === "outlook") {
      const wsId = readWorkspaceIdFromStorage() || "";
      if (!wsId) {
        alert("Could not connect Outlook: no active workspace. Try refreshing the page.");
        return;
      }
      launchConnectPopup(
        `${apiUrl}/api/outlook/connect?workspaceId=${encodeURIComponent(wsId)}`,
        "outlook-connect",
      );
    } else if (connection.id === "teams") {
      const wsId = readWorkspaceIdFromStorage() || "";
      if (!wsId) {
        alert("Could not connect Microsoft Teams: no active workspace. Try refreshing the page.");
        return;
      }
      launchConnectPopup(
        `${apiUrl}/api/teams/connect?workspaceId=${encodeURIComponent(wsId)}`,
        "teams-connect",
      );
    } else if (connection.id === "salesforce") {
      const wsId = readWorkspaceIdFromStorage() || "";
      if (!wsId) {
        alert("Could not connect Salesforce: no active workspace. Try refreshing the page.");
        return;
      }
      setSalesforceEnv("production");
      setSalesforceEnvOpen(true);
    } else if (connection.id === "sentry") {
      const wsId = readWorkspaceIdFromStorage() || "";
      if (!wsId) {
        alert("Could not connect Sentry: no active workspace. Try refreshing the page.");
        return;
      }
      launchConnectPopup(
        `${apiUrl}/api/sentry/connect?workspaceId=${encodeURIComponent(wsId)}`,
        "sentry-connect",
      );
    } else if (connection.id === "onedrive") {
      const wsId = readWorkspaceIdFromStorage() || "";
      if (!wsId) {
        alert("Could not connect OneDrive: no active workspace. Try refreshing the page.");
        return;
      }
      launchConnectPopup(
        `${apiUrl}/api/onedrive/connect?workspaceId=${encodeURIComponent(wsId)}`,
        "onedrive-connect",
      );
    } else if (connection.id === "confluence") {
      const wsId = readWorkspaceIdFromStorage() || "";
      if (!wsId) {
        alert("Could not connect Confluence: no active workspace. Try refreshing the page.");
        return;
      }
      launchConnectPopup(
        `${apiUrl}/api/confluence/connect?workspaceId=${encodeURIComponent(wsId)}`,
        "confluence-connect",
      );
    } else if (connection.id === "gitlab") {
      const wsId = readWorkspaceIdFromStorage() || "";
      if (!wsId) {
        alert("Could not connect GitLab: no active workspace. Try refreshing the page.");
        return;
      }
      launchConnectPopup(
        `${apiUrl}/api/gitlab/connect?workspaceId=${encodeURIComponent(wsId)}`,
        "gitlab-connect",
      );
    } else if (connection.id === "notion") {
      const wsId = readWorkspaceIdFromStorage() || "";
      if (!wsId) {
        alert("Could not connect Notion: no active workspace. Try refreshing the page.");
        return;
      }
      launchConnectPopup(
        `${apiUrl}/api/notion/connect?workspaceId=${encodeURIComponent(wsId)}`,
        "notion-connect",
      );
    } else if (connection.id === "pagerduty") {
      const wsId = readWorkspaceIdFromStorage() || "";
      if (!wsId) {
        alert("Could not connect PagerDuty: no active workspace. Try refreshing the page.");
        return;
      }
      launchConnectPopup(
        `${apiUrl}/api/pagerduty/connect?workspaceId=${encodeURIComponent(wsId)}`,
        "pagerduty-connect",
      );
    } else if (connection.id === "clickup") {
      const wsId = readWorkspaceIdFromStorage() || "";
      if (!wsId) {
        alert("Could not connect ClickUp: no active workspace. Try refreshing the page.");
        return;
      }
      launchConnectPopup(
        `${apiUrl}/api/clickup/connect?workspaceId=${encodeURIComponent(wsId)}`,
        "clickup-connect",
      );
    } else if (connection.id === "box") {
      const wsId = readWorkspaceIdFromStorage() || "";
      if (!wsId) {
        alert("Could not connect Box: no active workspace. Try refreshing the page.");
        return;
      }
      launchConnectPopup(
        `${apiUrl}/api/box/connect?workspaceId=${encodeURIComponent(wsId)}`,
        "box-connect",
      );
    } else if (connection.id === "trello") {
      const wsId = readWorkspaceIdFromStorage() || "";
      if (!wsId) {
        alert("Could not connect Trello: no active workspace. Try refreshing the page.");
        return;
      }
      launchConnectPopup(
        `${apiUrl}/api/trello/connect?workspaceId=${encodeURIComponent(wsId)}`,
        "trello-connect",
      );
    } else if (connection.id === "asana") {
      const wsId = readWorkspaceIdFromStorage() || "";
      if (!wsId) {
        alert("Could not connect Asana: no active workspace. Try refreshing the page.");
        return;
      }
      launchConnectPopup(
        `${apiUrl}/api/asana/connect?workspaceId=${encodeURIComponent(wsId)}`,
        "asana-connect",
      );
    } else if (connection.id === "zoom") {
      const wsId = readWorkspaceIdFromStorage() || "";
      if (!wsId) {
        alert("Could not connect Zoom: no active workspace. Try refreshing the page.");
        return;
      }
      launchConnectPopup(
        `${apiUrl}/api/zoom/connect?workspaceId=${encodeURIComponent(wsId)}`,
        "zoom-connect",
      );
    } else if (connection.id === "dropbox") {
      const wsId = readWorkspaceIdFromStorage() || "";
      if (!wsId) {
        alert("Could not connect Dropbox: no active workspace. Try refreshing the page.");
        return;
      }
      launchConnectPopup(
        `${apiUrl}/api/dropbox/connect?workspaceId=${encodeURIComponent(wsId)}`,
        "dropbox-connect",
      );
    } else if (connection.id === "figma") {
      const wsId = readWorkspaceIdFromStorage() || "";
      if (!wsId) {
        alert("Could not connect Figma: no active workspace. Try refreshing the page.");
        return;
      }
      launchConnectPopup(
        `${apiUrl}/api/figma/connect?workspaceId=${encodeURIComponent(wsId)}`,
        "figma-connect",
      );
    } else if (connection.id === "linear") {
      const wsId = readWorkspaceIdFromStorage() || "";
      if (!wsId) {
        alert("Could not connect Linear: no active workspace. Try refreshing the page.");
        return;
      }
      launchConnectPopup(
        `${apiUrl}/api/linear/connect?workspaceId=${encodeURIComponent(wsId)}`,
        "linear-connect",
      );
    } else if (connection.id === "intercom") {
      // Region first — the popup redirects straight to Intercom's regional
      // authorize host.
      setIntercomRegionOpen(true);
    } else if (connection.id === "jira") {
      // Auth-protected like Gmail/Drive: the popup carries the session
      // cookie; we only supply workspaceId.
      const wsId = readWorkspaceIdFromStorage() || "";
      if (!wsId) {
        alert("Could not connect Jira: no active workspace. Try refreshing the page.");
        return;
      }
      launchConnectPopup(
        `${apiUrl}/api/jira/connect?workspaceId=${encodeURIComponent(wsId)}`,
        "jira-connect",
      );
    } else if (connection.id === "google-drive") {
      // Auth-protected like Gmail: the popup carries the session cookie and
      // the backend derives the user from it; we only supply workspaceId.
      const wsId = readWorkspaceIdFromStorage() || "";
      if (!wsId) {
        alert("Could not connect Google Drive: no active workspace. Try refreshing the page.");
        return;
      }
      launchConnectPopup(
        `${apiUrl}/api/google-drive/connect?workspaceId=${encodeURIComponent(wsId)}`,
        "google-drive-connect",
      );
    } else if (connection.id === "gmail") {
      // Gmail's /api/gmail/connect on closot-api is auth-protected (@CurrentUser)
      // and pulls userId from the session cookie carried by the popup; we only
      // have to supply workspaceId via query string.
      const wsId = readWorkspaceIdFromStorage() || "";
      if (!wsId) {
        alert("Could not connect Gmail: no active workspace. Try refreshing the page.");
        return;
      }
      launchConnectPopup(`${apiUrl}/api/gmail/connect?workspaceId=${encodeURIComponent(wsId)}`, "gmail-connect");
    } else {
      console.log("Connect to", connection.id);
    }
  };

  const handleReconnect = (connection: Connection) => {
    setOpenMenuId(null);
    void startConnect(connection);
  };

  /**
   * Per-integration delete descriptors. Every id here corresponds to a
   * connected card that can appear in the list, and each maps to a real
   * backend DELETE route plus the React Query key to invalidate so the card
   * drops back to "Discover new connections" on success.
   */
  const disconnectConfig: Record<
    string,
    { confirmMessage: string; request: () => Promise<unknown>; invalidate: () => Promise<void> }
  > = {
    slack: {
      confirmMessage:
        "Delete the Slack connection?\n\nClosot will lose access to your Slack workspace. Link previews, notifications, and the /closot command stop working until you reconnect.",
      request: () => deleteWithAuth("/api/slack/connection"),
      invalidate: () => queryClient.invalidateQueries({ queryKey: integrationKeys.slack() }),
    },
    github: {
      confirmMessage:
        "Delete the GitHub connection?\n\nClosot will lose access to your GitHub account. PR and issue link previews stop working until you reconnect.",
      request: () => deleteWithAuth("/api/github/connection"),
      invalidate: () => queryClient.invalidateQueries({ queryKey: integrationKeys.github() }),
    },
    "google-calendar": {
      confirmMessage:
        "Delete the Google Calendar connection?\n\nThis stops syncing new calendar events. Already-scheduled bots and past meeting recordings are kept.",
      request: () => deleteWithAuth("/api/calendar/disconnect", { body: JSON.stringify({ workspaceId }) }),
      invalidate: () => queryClient.invalidateQueries({ queryKey: integrationKeys.calendar(workspaceId) }),
    },
    outlook: {
      confirmMessage:
        "Delete the Outlook connection?\n\nClosot will stop reading your Outlook calendar and mail. Nothing already in your pages is removed.",
      request: () => deleteWithAuth("/api/outlook/disconnect", { body: JSON.stringify({ workspaceId }) }),
      invalidate: () => queryClient.invalidateQueries({ queryKey: integrationKeys.outlook(workspaceId) }),
    },
    teams: {
      confirmMessage:
        "Delete the Microsoft Teams connection?\n\nMessage links already in your pages stay as links, but they stop showing live previews until you reconnect.",
      request: () => deleteWithAuth("/api/teams/disconnect", { body: JSON.stringify({ workspaceId }) }),
      invalidate: () => queryClient.invalidateQueries({ queryKey: integrationKeys.teams(workspaceId) }),
    },
    salesforce: {
      confirmMessage:
        "Delete the Salesforce connection?\n\nRecord links already in your pages stay as links, but they stop showing live previews until you reconnect.",
      request: () => deleteWithAuth("/api/salesforce/disconnect", { body: JSON.stringify({ workspaceId }) }),
      invalidate: () => queryClient.invalidateQueries({ queryKey: integrationKeys.salesforce(workspaceId) }),
    },
    sentry: {
      confirmMessage:
        "Delete the Sentry connection?\n\nLinks already in your pages stay as links, but they stop showing live previews until you reconnect.",
      request: () => deleteWithAuth("/api/sentry/disconnect", { body: JSON.stringify({ workspaceId }) }),
      invalidate: () => queryClient.invalidateQueries({ queryKey: integrationKeys.sentry(workspaceId) }),
    },
    onedrive: {
      confirmMessage:
        "Delete the OneDrive connection?\n\nFile links already in your pages stay as links, but they stop showing live previews until you reconnect.",
      request: () => deleteWithAuth("/api/onedrive/disconnect", { body: JSON.stringify({ workspaceId }) }),
      invalidate: () => queryClient.invalidateQueries({ queryKey: integrationKeys.onedrive(workspaceId) }),
    },
    confluence: {
      confirmMessage:
        "Delete the Confluence connection?\n\nAlready-imported pages stay in Closot. New space imports will need Confluence connected again.",
      request: () => deleteWithAuth("/api/confluence/disconnect", { body: JSON.stringify({ workspaceId }) }),
      invalidate: () => queryClient.invalidateQueries({ queryKey: integrationKeys.confluence(workspaceId) }),
    },
    gitlab: {
      confirmMessage:
        "Delete the GitLab connection?\n\nMerge request and issue links already in your pages stay as links, but they stop showing live previews until you reconnect.",
      request: () => deleteWithAuth("/api/gitlab/disconnect", { body: JSON.stringify({ workspaceId }) }),
      invalidate: () => queryClient.invalidateQueries({ queryKey: integrationKeys.gitlab(workspaceId) }),
    },
    notion: {
      confirmMessage:
        "Delete the Notion connection?\n\nAlready-imported pages stay in Closot. New imports will need Notion connected again.",
      request: () => deleteWithAuth("/api/notion/disconnect", { body: JSON.stringify({ workspaceId }) }),
      invalidate: () => queryClient.invalidateQueries({ queryKey: integrationKeys.notion(workspaceId) }),
    },
    pagerduty: {
      confirmMessage:
        "Delete the PagerDuty connection?\n\nIncident links already in your pages stay as links, but they stop showing live previews until you reconnect.",
      request: () => deleteWithAuth("/api/pagerduty/disconnect", { body: JSON.stringify({ workspaceId }) }),
      invalidate: () => queryClient.invalidateQueries({ queryKey: integrationKeys.pagerduty(workspaceId) }),
    },
    clickup: {
      confirmMessage:
        "Delete the ClickUp connection?\n\nTask links already in your pages stay as links, but they stop showing live previews until you reconnect.",
      request: () => deleteWithAuth("/api/clickup/disconnect", { body: JSON.stringify({ workspaceId }) }),
      invalidate: () => queryClient.invalidateQueries({ queryKey: integrationKeys.clickup(workspaceId) }),
    },
    box: {
      confirmMessage:
        "Delete the Box connection?\n\nFile and folder links already in your pages stay as links, but they stop showing live previews until you reconnect.",
      request: () => deleteWithAuth("/api/box/disconnect", { body: JSON.stringify({ workspaceId }) }),
      invalidate: () => queryClient.invalidateQueries({ queryKey: integrationKeys.box(workspaceId) }),
    },
    trello: {
      confirmMessage:
        "Delete the Trello connection?\n\nCard and board links already in your pages stay as links, but they stop showing live previews until you reconnect.",
      request: () => deleteWithAuth("/api/trello/disconnect", { body: JSON.stringify({ workspaceId }) }),
      invalidate: () => queryClient.invalidateQueries({ queryKey: integrationKeys.trello(workspaceId) }),
    },
    asana: {
      confirmMessage:
        "Delete the Asana connection?\n\nTask links already in your pages stay as links, but they stop showing live previews until you reconnect.",
      request: () => deleteWithAuth("/api/asana/disconnect", { body: JSON.stringify({ workspaceId }) }),
      invalidate: () => queryClient.invalidateQueries({ queryKey: integrationKeys.asana(workspaceId) }),
    },
    zoom: {
      confirmMessage:
        "Delete the Zoom connection?\n\nMeeting links already in your pages stay as links, but they stop showing live previews until you reconnect.",
      request: () => deleteWithAuth("/api/zoom/disconnect", { body: JSON.stringify({ workspaceId }) }),
      invalidate: () => queryClient.invalidateQueries({ queryKey: integrationKeys.zoom(workspaceId) }),
    },
    dropbox: {
      confirmMessage:
        "Delete the Dropbox connection?\n\nShare links already in your pages stay as links, but they stop showing live previews until you reconnect.",
      request: () => deleteWithAuth("/api/dropbox/disconnect", { body: JSON.stringify({ workspaceId }) }),
      invalidate: () => queryClient.invalidateQueries({ queryKey: integrationKeys.dropbox(workspaceId) }),
    },
    figma: {
      confirmMessage:
        "Delete the Figma connection?\n\nFile links already in your pages stay as links, but they stop showing live previews until you reconnect.",
      request: () => deleteWithAuth("/api/figma/disconnect", { body: JSON.stringify({ workspaceId }) }),
      invalidate: () => queryClient.invalidateQueries({ queryKey: integrationKeys.figma(workspaceId) }),
    },
    linear: {
      confirmMessage:
        "Delete the Linear connection?\n\nIssue links already in your pages stay as links, but they stop showing live previews until you reconnect.",
      request: () => deleteWithAuth("/api/linear/disconnect", { body: JSON.stringify({ workspaceId }) }),
      invalidate: () => queryClient.invalidateQueries({ queryKey: integrationKeys.linear(workspaceId) }),
    },
    intercom: {
      confirmMessage:
        "Delete the Intercom connection?\n\nConversation links already in your pages stay as links, but they stop showing live previews until you reconnect.",
      request: () => deleteWithAuth("/api/intercom/disconnect", { body: JSON.stringify({ workspaceId }) }),
      invalidate: () => queryClient.invalidateQueries({ queryKey: integrationKeys.intercom(workspaceId) }),
    },
    jira: {
      confirmMessage:
        "Delete the Jira connection?\n\nIssue links already in your pages stay as links, but they stop showing live previews until you reconnect.",
      request: () => deleteWithAuth("/api/jira/disconnect", { body: JSON.stringify({ workspaceId }) }),
      invalidate: () => queryClient.invalidateQueries({ queryKey: integrationKeys.jira(workspaceId) }),
    },
    "google-drive": {
      confirmMessage:
        "Delete the Google Drive connection?\n\nDrive links already in your pages stay as links, but they stop showing live previews until you reconnect.",
      request: () => deleteWithAuth("/api/google-drive/disconnect", { body: JSON.stringify({ workspaceId }) }),
      invalidate: () => queryClient.invalidateQueries({ queryKey: integrationKeys.googleDrive(workspaceId) }),
    },
    gmail: {
      confirmMessage:
        "Delete the Gmail connection?\n\nAgents will lose access to read, draft, and send email from this account until you reconnect.",
      request: () => deleteWithAuth("/api/gmail/disconnect", { body: JSON.stringify({ workspaceId }) }),
      invalidate: () => queryClient.invalidateQueries({ queryKey: integrationKeys.gmail(workspaceId) }),
    },
  };

  const handleDisconnect = async (connection: Connection) => {
    setOpenMenuId(null);
    const cfg = disconnectConfig[connection.id];
    if (!cfg) return;
    if (!window.confirm(cfg.confirmMessage)) return;

    setDisconnectingId(connection.id);
    try {
      const result = await cfg.request();
      if (result && typeof result === "object" && "isError" in result && (result as { isError?: boolean }).isError) {
        alert(`Failed to delete connection: ${(result as { message?: string }).message ?? "Unknown error"}`);
        return;
      }
      await cfg.invalidate();
    } finally {
      setDisconnectingId(null);
    }
  };

  const {
    connected: githubConnected,
    githubLogin,
    githubAvatarUrl,
    isLoading: isGithubLoading,
  } = useGithubStatus();

  const {
    connected: slackConnected,
    slackTeamName,
    isLoading: isSlackLoading,
  } = useSlackStatus();

  const {
    connected: calendarConnected,
    userEmail: calendarUserEmail,
    isLoading: isCalendarLoading,
  } = useCalendarStatus(workspaceId);


  const {
    connected: outlookConnected,
    isLoading: isOutlookLoading,
  } = useOutlookStatus(workspaceId);

  const {
    connected: teamsConnected,
    isLoading: isTeamsLoading,
  } = useTeamsStatus(workspaceId);

  const {
    connected: salesforceConnected,
    isLoading: isSalesforceLoading,
  } = useSalesforceStatus(workspaceId);

  const {
    connected: sentryConnected,
    isLoading: isSentryLoading,
  } = useSentryStatus(workspaceId);

  const {
    connected: onedriveConnected,
    isLoading: isOneDriveLoading,
  } = useOneDriveStatus(workspaceId);

  const {
    connected: confluenceConnected,
    isLoading: isConfluenceLoading,
  } = useConfluenceStatus(workspaceId);

  const {
    connected: gitlabConnected,
    isLoading: isGitLabLoading,
  } = useGitLabStatus(workspaceId);

  const {
    connected: notionConnected,
    isLoading: isNotionLoading,
  } = useNotionStatus(workspaceId);

  const {
    connected: pagerdutyConnected,
    isLoading: isPagerDutyLoading,
  } = usePagerDutyStatus(workspaceId);

  const {
    connected: clickupConnected,
    isLoading: isClickUpLoading,
  } = useClickUpStatus(workspaceId);

  const {
    connected: boxConnected,
    isLoading: isBoxLoading,
  } = useBoxStatus(workspaceId);

  const {
    connected: trelloConnected,
    isLoading: isTrelloLoading,
  } = useTrelloStatus(workspaceId);

  const {
    connected: asanaConnected,
    isLoading: isAsanaLoading,
  } = useAsanaStatus(workspaceId);

  const {
    connected: zoomConnected,
    isLoading: isZoomLoading,
  } = useZoomStatus(workspaceId);

  const {
    connected: dropboxConnected,
    isLoading: isDropboxLoading,
  } = useDropboxStatus(workspaceId);

  const {
    connected: figmaConnected,
    isLoading: isFigmaLoading,
  } = useFigmaStatus(workspaceId);

  const {
    connected: linearConnected,
    isLoading: isLinearLoading,
  } = useLinearStatus(workspaceId);

  const {
    connected: intercomConnected,
    isLoading: isIntercomLoading,
  } = useIntercomStatus(workspaceId);

  const {
    connected: jiraConnected,
    sites: jiraSites,
    isLoading: isJiraLoading,
  } = useJiraStatus(workspaceId);

  const {
    connected: driveConnected,
    userEmail: driveUserEmail,
    isLoading: isDriveLoading,
  } = useGoogleDriveStatus(workspaceId);

  const {
    connected: gmailConnected,
    userEmail: gmailUserEmail,
    isLoading: isGmailLoading,
  } = useGmailStatus(workspaceId);

  // Marketplace detail view (About / Features / How to use) for a connector.
  const [detailConnection, setDetailConnection] = useState<Connection | null>(null);

  const { connectors: catalogConnectors } = useConnectionsCatalog();

  /**
   * Gallery source of truth: the backend catalog (connector-kernel
   * manifests), falling back to the static AVAILABLE_CONNECTIONS while
   * loading, on error, or against an older backend without the endpoint.
   * Icons come from lib/connector-icons, not the catalog's iconUrl.
   */
  const allConnections = useMemo<Connection[]>(() => {
    if (catalogConnectors.length === 0) return AVAILABLE_CONNECTIONS;
    return catalogConnectors.map((c) => {
      const fallback = AVAILABLE_CONNECTIONS.find((a) => a.id === c.id);
      // Embed connectors (authKind "none") have no OAuth flow — the card
      // action opens the detail page explaining how to use them instead.
      const isEmbedOnly = c.authKind === "none";
      return {
        id: c.id,
        name: c.name,
        description: c.description,
        // Beta connectors are usable but pending provider review (e.g. Google
        // verification) — say so on the card rather than hiding them.
        badges: c.status === "beta" ? [...(c.badges ?? []), "Beta"] : c.badges,
        actionButtonText: isEmbedOnly ? "How to use" : "Connect",
        actionButtonVariant: isEmbedOnly
          ? ("explore" as const)
          : ("connect" as const),
        category: c.category || fallback?.category,
        listing: c.listing ?? undefined,
        authKind: c.authKind,
      };
    });
  }, [catalogConnectors]);

  const connectedConnections = useMemo(() => {
    const connected: Connection[] = [];

    // GitHub
    if (githubConnected) {
      const githubConnection = allConnections.find((c) => c.id === "github");
      if (githubConnection) {
        connected.push({
          ...githubConnection,
          isConnected: true,
          actionButtonText: "Manage",
        });
      }
    }

    // Slack
    if (slackConnected) {
      const slackConnection = allConnections.find((c) => c.id === "slack");
      if (slackConnection) {
        connected.push({
          ...slackConnection,
          isConnected: true,
          actionButtonText: "Manage",
          description: slackTeamName
            ? `Connected to ${slackTeamName}`
            : slackConnection.description,
        });
      }
    }

    // Google Calendar
    if (calendarConnected) {
      const calendarConnection = allConnections.find((c) => c.id === "google-calendar");
      if (calendarConnection) {
        connected.push({
          ...calendarConnection,
          isConnected: true,
          actionButtonText: "Manage",
          description: calendarUserEmail
            ? `Connected as ${calendarUserEmail}`
            : calendarConnection.description,
        });
      }
    }

    // Gmail
    if (gmailConnected) {
      const gmailConnection = allConnections.find((c) => c.id === "gmail");
      if (gmailConnection) {
        connected.push({
          ...gmailConnection,
          isConnected: true,
          actionButtonText: "Manage",
          description: gmailUserEmail
            ? `Connected as ${gmailUserEmail}`
            : gmailConnection.description,
        });
      }
    }

    // Google Drive
    if (driveConnected) {
      const driveConnection = allConnections.find((c) => c.id === "google-drive");
      if (driveConnection) {
        connected.push({
          ...driveConnection,
          isConnected: true,
          actionButtonText: "Manage",
          description: driveUserEmail
            ? `Connected as ${driveUserEmail}`
            : driveConnection.description,
        });
      }
    }

    // Jira
    if (jiraConnected) {
      const jiraConnection = allConnections.find((c) => c.id === "jira");
      if (jiraConnection) {
        connected.push({
          ...jiraConnection,
          isConnected: true,
          actionButtonText: "Manage",
          description:
            jiraSites.length > 0
              ? `Connected to ${jiraSites.map((s) => s.name).join(", ")}`
              : jiraConnection.description,
        });
      }
    }

    // Linear
    if (linearConnected) {
      const linearConnection = allConnections.find((c) => c.id === "linear");
      if (linearConnection) {
        connected.push({
          ...linearConnection,
          isConnected: true,
          actionButtonText: "Manage",
        });
      }
    }

    // Intercom
    if (intercomConnected) {
      const intercomConnection = allConnections.find((c) => c.id === "intercom");
      if (intercomConnection) {
        connected.push({
          ...intercomConnection,
          isConnected: true,
          actionButtonText: "Manage",
        });
      }
    }

    // Figma
    if (figmaConnected) {
      const figmaConnection = allConnections.find((c) => c.id === "figma");
      if (figmaConnection) {
        connected.push({
          ...figmaConnection,
          isConnected: true,
          actionButtonText: "Manage",
        });
      }
    }

    // Dropbox
    if (dropboxConnected) {
      const dropboxConnection = allConnections.find((c) => c.id === "dropbox");
      if (dropboxConnection) {
        connected.push({
          ...dropboxConnection,
          isConnected: true,
          actionButtonText: "Manage",
        });
      }
    }

    // Zoom
    if (zoomConnected) {
      const zoomConnection = allConnections.find((c) => c.id === "zoom");
      if (zoomConnection) {
        connected.push({
          ...zoomConnection,
          isConnected: true,
          actionButtonText: "Manage",
        });
      }
    }

    // Asana
    if (asanaConnected) {
      const asanaConnection = allConnections.find((c) => c.id === "asana");
      if (asanaConnection) {
        connected.push({
          ...asanaConnection,
          isConnected: true,
          actionButtonText: "Manage",
        });
      }
    }

    // Trello
    if (trelloConnected) {
      const trelloConnection = allConnections.find((c) => c.id === "trello");
      if (trelloConnection) {
        connected.push({
          ...trelloConnection,
          isConnected: true,
          actionButtonText: "Manage",
        });
      }
    }

    // Box
    if (boxConnected) {
      const boxConnection = allConnections.find((c) => c.id === "box");
      if (boxConnection) {
        connected.push({
          ...boxConnection,
          isConnected: true,
          actionButtonText: "Manage",
        });
      }
    }

    // ClickUp
    if (clickupConnected) {
      const clickupConnection = allConnections.find((c) => c.id === "clickup");
      if (clickupConnection) {
        connected.push({
          ...clickupConnection,
          isConnected: true,
          actionButtonText: "Manage",
        });
      }
    }

    // PagerDuty
    if (pagerdutyConnected) {
      const pagerdutyConnection = allConnections.find((c) => c.id === "pagerduty");
      if (pagerdutyConnection) {
        connected.push({
          ...pagerdutyConnection,
          isConnected: true,
          actionButtonText: "Manage",
        });
      }
    }

    // Notion (importer)
    if (notionConnected) {
      const notionConnection = allConnections.find((c) => c.id === "notion");
      if (notionConnection) {
        connected.push({
          ...notionConnection,
          isConnected: true,
          actionButtonText: "Manage",
        });
      }
    }

    // GitLab
    if (gitlabConnected) {
      const gitlabConnection = allConnections.find((c) => c.id === "gitlab");
      if (gitlabConnection) {
        connected.push({
          ...gitlabConnection,
          isConnected: true,
          actionButtonText: "Manage",
        });
      }
    }

    // Confluence
    if (confluenceConnected) {
      const confluenceConnection = allConnections.find((c) => c.id === "confluence");
      if (confluenceConnection) {
        connected.push({
          ...confluenceConnection,
          isConnected: true,
          actionButtonText: "Manage",
        });
      }
    }

    // OneDrive / SharePoint
    if (onedriveConnected) {
      const onedriveConnection = allConnections.find((c) => c.id === "onedrive");
      if (onedriveConnection) {
        connected.push({
          ...onedriveConnection,
          isConnected: true,
          actionButtonText: "Manage",
        });
      }
    }

    // Sentry
    if (sentryConnected) {
      const sentryConnection = allConnections.find((c) => c.id === "sentry");
      if (sentryConnection) {
        connected.push({
          ...sentryConnection,
          isConnected: true,
          actionButtonText: "Manage",
        });
      }
    }

    // Salesforce
    if (salesforceConnected) {
      const salesforceConnection = allConnections.find((c) => c.id === "salesforce");
      if (salesforceConnection) {
        connected.push({
          ...salesforceConnection,
          isConnected: true,
          actionButtonText: "Manage",
        });
      }
    }

    // Microsoft Teams
    if (teamsConnected) {
      const teamsConnection = allConnections.find((c) => c.id === "teams");
      if (teamsConnection) {
        connected.push({
          ...teamsConnection,
          isConnected: true,
          actionButtonText: "Manage",
        });
      }
    }

    // Outlook
    if (outlookConnected) {
      const outlookConnection = allConnections.find((c) => c.id === "outlook");
      if (outlookConnection) {
        connected.push({
          ...outlookConnection,
          isConnected: true,
          actionButtonText: "Manage",
        });
      }
    }

    return connected;
  }, [allConnections, githubConnected, slackConnected, slackTeamName, calendarConnected, calendarUserEmail, gmailConnected, gmailUserEmail, driveConnected, driveUserEmail, jiraConnected, jiraSites, linearConnected, intercomConnected, figmaConnected, dropboxConnected, zoomConnected, asanaConnected, trelloConnected, boxConnected, clickupConnected, pagerdutyConnected, notionConnected, gitlabConnected, confluenceConnected, onedriveConnected, sentryConnected, salesforceConnected, teamsConnected, outlookConnected]);

  // Get recommended connections (all available connections that are not connected)
  const recommendedConnections = useMemo(() => {
    const connectedIds = new Set(connectedConnections.map((c) => c.id));
    return allConnections.filter((c) => !connectedIds.has(c.id));
  }, [allConnections, connectedConnections]);

  const handleConnectionAction = (connection: Connection) => {
    // Embed connectors have nothing to connect — send the click to the
    // marketplace detail page, which documents how to use them.
    if (connection.authKind === "none") {
      setDetailConnection(connection);
      return;
    }
    // Connected cards open the Reconnect / Delete menu; unconnected cards
    // start the OAuth connect flow directly.
    if (connection.isConnected) {
      // Ignore clicks while a delete for this card is in flight — keep the
      // menu closed so the user can't fire Reconnect (OAuth) mid-delete.
      if (disconnectingId === connection.id) return;
      setOpenMenuId((prev) => (prev === connection.id ? null : connection.id));
      return;
    }
    void startConnect(connection);
  };

  const renderIcon = (connection: Connection) => {
    // A bespoke mark wins; otherwise the id resolves against
    // lib/connector-icons, the same registry the editor cards use.
    if (connection.icon) {
      return <div className="w-9 h-9 flex items-center justify-center">{connection.icon}</div>;
    }
    return (
      <div className="w-9 h-9 p-1 flex items-center justify-center">
        <BrandIcon id={connection.id} size={28} alt={connection.name} />
      </div>
    );
  };

  const hasConnections = connectedConnections.length > 0;
  const isLoading = isGithubLoading || isSlackLoading || isCalendarLoading || isGmailLoading || isDriveLoading || isJiraLoading || isLinearLoading || isIntercomLoading || isFigmaLoading || isDropboxLoading || isZoomLoading || isAsanaLoading || isTrelloLoading || isBoxLoading || isClickUpLoading || isPagerDutyLoading || isNotionLoading || isGitLabLoading || isConfluenceLoading || isOneDriveLoading || isSentryLoading || isSalesforceLoading || isTeamsLoading || isOutlookLoading;

  if (isLoading) {
    return (
      <div className="flex flex-col h-full w-auto">
        <div className="flex-grow transform translate-z-0 overflow-auto clean-scrollbar">
          <div className="mb-8">
            <div className="h-5 w-24 rounded animate-pulse bg-[rgb(225,225,224)] dark:bg-[rgb(45,45,45)] mb-4" />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 p-4 rounded-xl border border-component">
                  <div className="w-9 h-9 rounded-md animate-pulse bg-[rgb(225,225,224)] dark:bg-[rgb(45,45,45)] flex-shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3.5 w-20 rounded animate-pulse bg-[rgb(225,225,224)] dark:bg-[rgb(45,45,45)]" />
                    <div className="h-3 w-32 rounded animate-pulse bg-[rgb(225,225,224)] dark:bg-[rgb(45,45,45)]" />
                  </div>
                  <div className="h-7 w-16 rounded-md animate-pulse bg-[rgb(225,225,224)] dark:bg-[rgb(45,45,45)]" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full w-auto">
      <div className="flex-grow transform translate-z-0 overflow-auto clean-scrollbar">
        {/* Connected Connections Section */}
        {hasConnections && (
          <div className="mb-8">
            <div className="border-b-0 mb-2 mt-0 pb-0 text-sm font-normal w-auto text-zinc-900 dark:text-zinc-100">
              <div className="flex justify-between min-h-7 text-base font-medium">
                Connected
              </div>
            </div>
            <ConnectionCardGrid>
              {connectedConnections.map((connection) => {
                const isMenuOpen = openMenuId === connection.id;
                const isThisDisconnecting = disconnectingId === connection.id;
                const canDisconnect = !!disconnectConfig[connection.id];

                return (
                  <div key={connection.id}>
                    <ConnectionCard
                      name={connection.name}
                      icon={renderIcon(connection)}
                      description={connection.description}
                      badges={connection.badges}
                      actionButtonText={isThisDisconnecting ? "Deleting…" : connection.actionButtonText}
                      actionButtonVariant={connection.actionButtonVariant}
                      onAction={() => handleConnectionAction(connection)}
                      onOpen={() => setDetailConnection(connection)}
                    />
                    {isMenuOpen && (
                      <SelfAnchoredPopover
                        role="menu"
                        placement="top-end"
                        onClose={() => setOpenMenuId(null)}
                        className="min-w-[160px] rounded-md border border-zinc-200 bg-white shadow-lg dark:border-zinc-700 dark:bg-zinc-900"
                      >
                        <button
                          type="button"
                          role="menuitem"
                          disabled={isThisDisconnecting}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleReconnect(connection);
                          }}
                          className="block w-full px-3 py-2 text-left text-sm text-zinc-700 hover-component disabled:opacity-50 dark:text-zinc-200 "
                        >
                          Reconnect
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          disabled={!canDisconnect || isThisDisconnecting}
                          onClick={(e) => {
                            e.stopPropagation();
                            void handleDisconnect(connection);
                          }}
                          className="block w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 disabled:opacity-50 dark:text-red-400 dark:hover:bg-red-950/30"
                        >
                          {isThisDisconnecting ? "Deleting…" : "Delete"}
                        </button>
                      </SelfAnchoredPopover>
                    )}
                  </div>
                );
              })}
            </ConnectionCardGrid>
          </div>
        )}

        {/* Recommended Connections Section */}
        <div>
          <div className="border-b-0 mb-2 mt-0 pb-0 text-sm font-normal w-auto text-zinc-900 dark:text-zinc-100">
            <div className="flex justify-between min-h-7 text-base font-medium">
              {hasConnections ? "Discover new connections" : "Connections"}
            </div>
          </div>
          <ConnectionCardGrid>  
            {/* Regular Connection Cards */}
            {recommendedConnections.map((connection) => (
              <ConnectionCard
                key={connection.id}
                name={connection.name}
                icon={renderIcon(connection)}
                description={connection.description}
                badges={connection.badges}
                actionButtonText={connection.actionButtonText}
                actionButtonVariant={connection.actionButtonVariant}
                onAction={() => handleConnectionAction(connection)}
                onOpen={() => setDetailConnection(connection)}
              />
            ))}
          </ConnectionCardGrid>
        </div>

      </div>

      {/* Marketplace detail view — About / Features / How to use */}
      <ConnectionDetailModal
        connection={detailConnection}
        isConnected={
          !!detailConnection &&
          connectedConnections.some((c) => c.id === detailConnection.id)
        }
        onClose={() => setDetailConnection(null)}
        onConnect={(connection) => {
          setDetailConnection(null);
          void startConnect(connection);
        }}
      />

      <DeleteConfirmationModal
        isOpen={salesforceEnvOpen}
        header="Connect Salesforce"
        message="Which Salesforce environment are you connecting?"
        confirmButtonText="Continue"
        confirmButtonColor="blue"
        options={[
          { value: "production", label: "Production", description: "login.salesforce.com — your live org" },
          { value: "sandbox", label: "Sandbox", description: "test.salesforce.com — a test or developer org" },
        ]}
        selectedOption={salesforceEnv}
        onOptionChange={(value) => setSalesforceEnv(value as "production" | "sandbox")}
        onCancel={() => setSalesforceEnvOpen(false)}
        onConfirm={() => launchSalesforceConnect(salesforceEnv)}
      />

      <DeleteConfirmationModal
        isOpen={intercomRegionOpen}
        header="Connect Intercom"
        message="Where is your Intercom workspace hosted?"
        confirmButtonText="Continue"
        confirmButtonColor="blue"
        options={[
          { value: "us", label: "US", description: "app.intercom.com — the default for most workspaces" },
          { value: "eu", label: "EU", description: "app.eu.intercom.com — EU data hosting" },
          { value: "au", label: "Australia", description: "app.au.intercom.com — Australian data hosting" },
        ]}
        selectedOption={intercomRegion}
        onOptionChange={(value) => setIntercomRegion(value as "us" | "eu" | "au")}
        onCancel={() => setIntercomRegionOpen(false)}
        onConfirm={() => launchIntercomConnect(intercomRegion)}
      />
    </div>
  );
}

